import { createLogger } from '@ctrl-enter/tools/utils';
import * as React from 'react';
import { useAuth } from '../auth';
import {
  getTokenExpiry,
  isTokenExpiringSoon,
  useSetError,
  useSetLoading,
  useSetToken,
  useToken,
  useTokenError,
  useTokenExpiry,
  useTokenLoading,
} from '../stores/widget-token-store';

const logger = createLogger('web:hook:use-widget-token');

/**
 * Configuration for widget token lifecycle management
 */
type WidgetTokenConfig = {
  /** How many minutes before expiry to refresh (default: 5) */
  refreshBeforeMinutes?: number;
  /** Enable auto-refresh on visibility change (default: true) */
  refreshOnVisibility?: boolean;
  /** Enable logging for debugging (default: false) */
  enableLogging?: boolean;
};

/**
 * Custom hook for managing WorkOS widget token lifecycle
 *
 * This is a lifecycle manager that connects to the centralized widget token store.
 * It handles automatic token generation, proactive refresh, and visibility-based re-validation.
 *
 * Features:
 * - Automatic token generation on mount
 * - Proactive refresh before expiry (T-5 minutes by default)
 * - Re-validation on page visibility/focus
 * - Centralized state via Zustand store (single token for all widgets)
 * - Observability logging
 *
 * @example
 * ```tsx
 * // In your component, just call the hook to activate lifecycle management
 * useWidgetToken({
 * refreshBeforeMinutes: 5,
 * refreshOnVisibility: true,
 * enableLogging: true
 * });
 *
 * // Then use store selectors to access the token
 * const token = useToken();
 * const isLoading = useTokenLoading();
 * const error = useTokenError();
 *
 * if (isLoading) return <div>Loading...</div>;
 * if (error) return <div>Error: {error.message}</div>;
 * if (!token) return null;
 *
 * return <UserProfile authToken={token} />;
 * ```
 */
export const useWidgetToken = (config: WidgetTokenConfig = {}) => {
  const { refreshBeforeMinutes = 5, refreshOnVisibility = true, enableLogging = false } = config;

  const { isAuthenticated, generateWidgetToken } = useAuth();

  // Get store state and actions via selectors
  const token = useToken();
  const expiresAt = useTokenExpiry();
  const isLoading = useTokenLoading();
  const error = useTokenError();
  const setToken = useSetToken();
  const setLoading = useSetLoading();
  const setError = useSetError();

  const [lastIdleTime, setLastIdleTime] = React.useState<number>(Date.now());
  const refreshIntervalRef = React.useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = React.useRef(true);
  const fetchInProgressRef = React.useRef<Promise<void> | null>(null);

  const log = React.useCallback(
    (...args: unknown[]) => {
      if (enableLogging) {
        logger.info('🎫 [WIDGET-TOKEN-LIFECYCLE]', ...args);
      }
    },
    [enableLogging]
  );

  const logCritical = React.useCallback((...args: unknown[]) => {
    logger.info('🎫 [WIDGET-TOKEN-LIFECYCLE] [CRITICAL]', ...args);
  }, []);

  /**
   * Generate or refresh the widget token
   */
  const fetchToken = React.useCallback(
    async (isRetry = false) => {
      // Deduplicate concurrent requests
      if (fetchInProgressRef.current) {
        log('Token fetch already in progress, waiting for existing request...');
        return fetchInProgressRef.current;
      }

      if (!isAuthenticated) {
        log('Not authenticated, skipping token fetch');
        return;
      }

      if (!isMountedRef.current) {
        log('Component unmounted, skipping token fetch');
        return;
      }

      const fetchPromise = (async () => {
        logCritical(
          'Starting token fetch...',
          JSON.stringify({ isRetry, isAuthenticated, isMounted: isMountedRef.current })
        );
        setLoading(true);

        try {
          log(isRetry ? 'Retrying token generation...' : 'Generating new token...');
          logCritical('Calling generateWidgetToken()...');

          const newToken = await generateWidgetToken();

          logCritical(
            'Received token from generateWidgetToken:',
            JSON.stringify(
              {
                hasToken: !!newToken,
                tokenLength: newToken?.length || 0,
                tokenPreview: newToken ? `${newToken.substring(0, 50)}...` : 'null',
              },
              null,
              2
            )
          );

          if (!newToken) {
            throw new Error('Failed to generate widget token: empty response');
          }

          const newExpiresAt = getTokenExpiry(newToken);

          logCritical(
            'Decoded token expiry:',
            JSON.stringify(
              {
                expiresAt: newExpiresAt,
                expiresAtDate: newExpiresAt ? new Date(newExpiresAt * 1000).toISOString() : 'null',
                isExpired: newExpiresAt ? Date.now() / 1000 > newExpiresAt : 'unknown',
              },
              null,
              2
            )
          );

          // Validate that JWT decode succeeded - if not, token is invalid
          if (!newExpiresAt) {
            throw new Error(
              'Failed to decode widget token: invalid JWT format or missing exp claim'
            );
          }

          log(
            'Token generated successfully',
            JSON.stringify(
              {
                expiresAt: new Date(newExpiresAt * 1000).toISOString(),
                expiresIn: `${Math.floor((newExpiresAt - Date.now() / 1000) / 60)} minutes`,
              },
              null,
              2
            )
          );

          if (isMountedRef.current) {
            logCritical(
              'Updating store with new token:',
              JSON.stringify(
                {
                  tokenLength: newToken.length,
                  expiresAt: newExpiresAt,
                  willRenderWidgets: true,
                },
                null,
                2
              )
            );
            setToken(newToken, newExpiresAt);
          } else {
            logCritical('Component unmounted, skipping store update');
          }
        } catch (error) {
          const errorObj = error instanceof Error ? error : new Error(String(error));
          logger.error('❌ [WIDGET-TOKEN-LIFECYCLE] Failed to generate token:', errorObj);
          logCritical(
            'Token generation failed:',
            JSON.stringify(
              {
                errorMessage: errorObj.message,
                errorStack: errorObj.stack,
              },
              null,
              2
            )
          );

          if (isMountedRef.current) {
            setError(errorObj);
          }
        } finally {
          fetchInProgressRef.current = null;
          logCritical('Token fetch complete');
        }
      })();

      fetchInProgressRef.current = fetchPromise;
      return fetchPromise;
    },
    [isAuthenticated, generateWidgetToken, setToken, setLoading, setError, log, logCritical]
  );

  /**
   * Check if token needs refresh and refresh if necessary
   */
  const checkAndRefreshToken = React.useCallback(async () => {
    if (!token || !expiresAt) {
      log('No token or expiry, fetching new token');
      await fetchToken();
      return;
    }

    const needsRefresh = isTokenExpiringSoon(expiresAt, refreshBeforeMinutes);

    if (needsRefresh) {
      const now = Math.floor(Date.now() / 1000);
      const minutesUntilExpiry = Math.floor((expiresAt - now) / 60);

      log(`Token expiring soon (${minutesUntilExpiry} minutes remaining), refreshing...`);
      await fetchToken();
    }
  }, [token, expiresAt, refreshBeforeMinutes, fetchToken, log]);

  /**
   * Handle visibility change (tab becomes visible or app gets focus)
   */
  const handleVisibilityChange = React.useCallback(async () => {
    if (document.hidden || !refreshOnVisibility) {
      // Document is hidden, track idle time
      setLastIdleTime(Date.now());
      return;
    }

    // Document became visible
    const idleDuration = Date.now() - lastIdleTime;
    const idleMinutes = Math.floor(idleDuration / 1000 / 60);

    log(`Page became visible after ${idleMinutes} minutes idle`);

    // If idle for more than 5 minutes, check token validity
    if (idleMinutes >= 5) {
      log('Long idle period detected, re-validating token...');
      await checkAndRefreshToken();
    }
  }, [lastIdleTime, refreshOnVisibility, checkAndRefreshToken, log]);

  /**
   * Initial token fetch on mount
   */
  const hasInitialFetchRef = React.useRef(false);

  React.useEffect(() => {
    if (isAuthenticated && !hasInitialFetchRef.current) {
      hasInitialFetchRef.current = true;
      log('Lifecycle manager initialized, fetching initial token');
      fetchToken();
    }

    // Reset flag if user logs out
    if (!isAuthenticated) {
      hasInitialFetchRef.current = false;
    }
  }, [isAuthenticated, fetchToken, log]);

  /**
   * Set up periodic check for token expiry (every minute)
   */
  const checkAndRefreshRef = React.useRef(checkAndRefreshToken);
  checkAndRefreshRef.current = checkAndRefreshToken;

  React.useEffect(() => {
    // Clear existing interval if no token
    if (!token || !expiresAt) {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
      return;
    }

    // Only create interval if it doesn't exist
    if (!refreshIntervalRef.current) {
      log('Setting up periodic token check (every 1 minute)');
      refreshIntervalRef.current = setInterval(() => {
        checkAndRefreshRef.current();
      }, 60 * 1000); // 1 minute
    }

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    };
  }, [token, expiresAt, log]);

  /**
   * Listen for visibility changes
   */
  React.useEffect(() => {
    if (!refreshOnVisibility) {
      return;
    }

    log('Setting up visibility change listeners');
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleVisibilityChange);
    };
  }, [refreshOnVisibility, handleVisibilityChange, log]);

  /**
   * Cleanup on unmount
   */
  React.useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, []);

  return {
    token,
    expiresAt,
    isLoading,
    error,
    refresh: fetchToken,
    checkAndRefresh: checkAndRefreshToken, // For manual testing
  };
};

export type { WidgetTokenConfig };
